import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function StatCard({ title, value, icon, color, link }) {
  const Card = link ? Link : 'div';
  return (
    <Card
      to={link}
      className={`card flex items-center gap-4 ${link ? 'hover:shadow-lg transition-shadow cursor-pointer' : ''}`}
    >
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${color}`}>
        {icon}
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-500">{title}</div>
      </div>
    </Card>
  );
}

function ScadenzaRow({ scadenza }) {
  const getStatoBadge = (stato) => {
    const badges = {
      100000000: 'badge-info',      // Pianificato
      100000001: 'badge-warning',   // In scadenza
      100000002: 'badge-danger',    // Scaduto
      100000003: 'badge-success',   // Eseguito
      100000004: 'badge-gray'       // Annullato
    };
    const labels = {
      100000000: 'Pianificato',
      100000001: 'In scadenza',
      100000002: 'Scaduto',
      100000003: 'Eseguito',
      100000004: 'Annullato'
    };
    return (
      <span className={badges[stato] || 'badge-gray'}>
        {labels[stato] || 'N/D'}
      </span>
    );
  };

  return (
    <tr>
      <td className="font-medium">{scadenza.nome}</td>
      <td>{scadenza.rsa?.nome || '-'}</td>
      <td>{new Date(scadenza.dataScadenza).toLocaleDateString('it-IT')}</td>
      <td>{getStatoBadge(scadenza.stato)}</td>
    </tr>
  );
}

// Ruoli
const ROLES = {
  ADMIN: 100000000,
  RSPP: 100000001,
  DIREZIONE: 100000002,
  DIRETTORE_RSA: 100000003,
  AMMINISTRATIVO: 100000004,
  FORNITORE: 100000005
};

function DashboardPage() {
  const { user, apiRequest } = useAuth();
  const [stats, setStats] = useState({
    rsa: 0,
    fornitori: 0,
    scadenzeProssime: 0,
    scadenzeScadute: 0,
    interventiDaValidare: 0
  });
  const [scadenzeProssime, setScadenzeProssime] = useState([]);
  const [interventiRecenti, setInterventiRecenti] = useState([]);
  const [loading, setLoading] = useState(true);

  const userRuolo = user?.ruolo;
  const isFornitore = userRuolo === ROLES.FORNITORE;
  const isAmministrativo = userRuolo === ROLES.AMMINISTRATIVO;
  const canSeeRsa = [ROLES.ADMIN, ROLES.RSPP, ROLES.DIREZIONE, ROLES.DIRETTORE_RSA, ROLES.AMMINISTRATIVO].includes(userRuolo);
  const canSeeFornitori = [ROLES.ADMIN, ROLES.RSPP, ROLES.DIREZIONE, ROLES.DIRETTORE_RSA].includes(userRuolo);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch base data (scadenze e interventi sono filtrati dal backend per ruolo)
        const promises = [
          apiRequest('/scadenze/prossime').catch(() => ({ value: [] })),
          apiRequest('/scadenze/scadute').catch(() => ({ value: [] })),
          apiRequest('/interventi/recenti').catch(() => ({ value: [] }))
        ];

        // Fetch RSA e Fornitori solo per ruoli che li vedono
        if (canSeeRsa) {
          promises.push(apiRequest('/rsa').catch(() => ({ count: 0 })));
        }
        if (canSeeFornitori) {
          promises.push(apiRequest('/fornitori').catch(() => ({ count: 0 })));
        }

        const results = await Promise.all(promises);

        const [scadenzeRes, scaduteRes, interventiRes] = results;
        const rsaRes = canSeeRsa ? results[3] : null;
        const fornitoriRes = canSeeFornitori ? results[canSeeRsa ? 4 : 3] : null;

        setStats({
          rsa: rsaRes ? (rsaRes.count || rsaRes.value?.length || 0) : 0,
          fornitori: fornitoriRes ? (fornitoriRes.count || fornitoriRes.value?.length || 0) : 0,
          scadenzeProssime: scadenzeRes.value?.length || 0,
          scadenzeScadute: scaduteRes.value?.length || 0
        });

        setScadenzeProssime(scadenzeRes.value?.slice(0, 5) || []);
        setInterventiRecenti(interventiRes.value?.slice(0, 5) || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [apiRequest, canSeeRsa, canSeeFornitori]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Welcome header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Benvenuto, {user?.displayName || 'Utente'}
        </h1>
        <p className="text-gray-500">
          {user?.ruoloLabel} {user?.rsa ? `- ${user.rsa.nome}` : ''} {user?.fornitore ? `- ${user.fornitore.nome}` : ''}
        </p>
      </div>

      {/* Stats cards - filtrate per ruolo */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${canSeeRsa || canSeeFornitori ? 'lg:grid-cols-4' : 'lg:grid-cols-2'} gap-4 mb-6`}>
        {canSeeRsa && (
          <StatCard
            title="RSA Attive"
            value={stats.rsa}
            icon="🏥"
            color="bg-blue-100"
            link="/rsa"
          />
        )}
        {canSeeFornitori && (
          <StatCard
            title="Fornitori"
            value={stats.fornitori}
            icon="🏢"
            color="bg-green-100"
            link="/fornitori"
          />
        )}
        <StatCard
          title={isFornitore ? "Le mie scadenze" : "Scadenze Prossime"}
          value={stats.scadenzeProssime}
          icon="📅"
          color="bg-yellow-100"
          link="/scadenze"
        />
        <StatCard
          title="Scadenze Scadute"
          value={stats.scadenzeScadute}
          icon="⚠️"
          color="bg-red-100"
          link="/scadenze?stato=100000002"
        />
      </div>

      {/* Two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Prossime scadenze */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Prossime Scadenze</h2>
            <Link to="/scadenze" className="text-primary-600 text-sm hover:underline">
              Vedi tutte
            </Link>
          </div>
          {scadenzeProssime.length > 0 ? (
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>RSA</th>
                  <th>Data</th>
                  <th>Stato</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {scadenzeProssime.map((s) => (
                  <ScadenzaRow key={s.id} scadenza={s} />
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500 text-center py-4">Nessuna scadenza nei prossimi 30 giorni</p>
          )}
        </div>

        {/* Interventi recenti */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Interventi Recenti</h2>
            <Link to="/interventi" className="text-primary-600 text-sm hover:underline">
              Vedi tutti
            </Link>
          </div>
          {interventiRecenti.length > 0 ? (
            <div className="space-y-3">
              {interventiRecenti.map((i) => (
                <div key={i.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium">{i.nome}</div>
                    <div className="text-sm text-gray-500">
                      {i.rsa?.nome} - {new Date(i.dataIntervento).toLocaleDateString('it-IT')}
                    </div>
                  </div>
                  <span className={i.validato ? 'badge-success' : 'badge-warning'}>
                    {i.validato ? 'Validato' : 'Da validare'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">Nessun intervento recente</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
